import { useState, useEffect } from 'react'
import { ExerciseDetail } from '@renderer/types/api'
import { appInfo } from '@renderer/config/appinfo'

interface UseLabGuideResult {
  pdfUrl: string | null
  pageStart: number | null
  pageEnd: number | null
  moduleTitle: string | null
  moduleRefCode: string | null
  courseId: number | null
  moduleId: number | null
  moduleOrderIndex: number | null
  courseName: string | null
  isLoading: boolean
  error: string | null
}

export const useLabGuide = (exercise: ExerciseDetail | null): UseLabGuideResult => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pageStart, setPageStart] = useState<number | null>(null)
  const [pageEnd, setPageEnd] = useState<number | null>(null)
  const [moduleTitle, setModuleTitle] = useState<string | null>(null)
  const [courseId, setCourseId] = useState<number | null>(null)
  const [moduleId, setModuleId] = useState<number | null>(null)
  const [moduleOrderIndex, setModuleOrderIndex] = useState<number | null>(null)
  const [moduleRefCode, setModuleRefCode] = useState<string | null>(null)
  const [courseName, setCourseName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!exercise) return

    let cancelled = false

    const fetchLabGuide = async () => {
      setIsLoading(true)
      setError(null)
      setPdfUrl(null)
      setPageStart(null)
      setPageEnd(null)
      setModuleTitle(null)
      setCourseId(null)
      setModuleId(null)
      setModuleOrderIndex(null)
      setCourseName(null)

      try {
        const moduleRes = await fetch(
          `${appInfo.apiDomain}/api/labs/labmodules/${exercise.lab_module_id}`,
          { credentials: 'include' }
        )

        if (!moduleRes.ok) throw new Error('Failed to fetch module info')
        const moduleData = await moduleRes.json()
        const fetchedCourseId: number = moduleData.course_id
        const modulePageStart: number | null = moduleData.page_start ?? null
        const modulePageEnd: number | null = moduleData.page_end ?? null
        const fetchedModuleTitle: string | null = moduleData.title ?? null

        setCourseId(fetchedCourseId)
        setModuleId(exercise.lab_module_id)
        setModuleOrderIndex(moduleData.order_index ?? null)
        setModuleRefCode(moduleData.ref_code ?? null)
        setModuleTitle(fetchedModuleTitle)

        const [docsRes, courseRes] = await Promise.all([
          fetch(`${appInfo.apiDomain}/documents/list/${fetchedCourseId}`, { credentials: 'include' }),
          fetch(`${appInfo.apiDomain}/courses/${fetchedCourseId}`, { credentials: 'include' })
        ])

        if (!docsRes.ok) throw new Error('Failed to fetch course documents')
        const docsData = await docsRes.json()
        const documents: Array<{ id: string; url: string; document_type: string | null }> =
          docsData.documents || []

        if (courseRes.ok) {
          const courseData = await courseRes.json()
          setCourseName(courseData.title ?? null)
        }

        const labGuide = documents.find(d => d.document_type === 'lab_guide')

        const resolveUrl = (url: string): string => {
          if (url.startsWith('http://') || url.startsWith('https://')) {
            return url
          }
          const cleanUrl = url.replace(/^\/+/, '')
          return `${appInfo.apiDomain}/storage/${cleanUrl}`
        }

        if (labGuide?.url) {
          setPdfUrl(resolveUrl(labGuide.url))
          setPageStart(modulePageStart)
          setPageEnd(modulePageEnd)
        } else {
          setPdfUrl(null)
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to load lab guide')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchLabGuide()
    return () => { cancelled = true }
  }, [exercise?.lab_module_id])

  return { pdfUrl, pageStart, pageEnd, moduleTitle, moduleRefCode, courseId, moduleId, moduleOrderIndex, courseName, isLoading, error }
}